import { useEffect, useState, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "../../utils/api.js";

interface JobRow {
  slug: string;
  title: string;
  team: string;
  location: string;
  status: string;
  hiringManager: string;
  hiringManagerName: string;
  candidateCount: number;
  assignmentId: number | null;
  assignmentTitle: string | null;
  assignmentPdfPath: string | null;
}

const STATUS_COLORS: Record<string, string> = {
  published: "bg-green-100 text-green-800",
  closed: "bg-red-100 text-red-800",
  draft: "bg-gray-100 text-gray-600",
};

const NEXT_STATUS: Record<string, { label: string; status: string }> = {
  draft: { label: "Publish", status: "published" },
  published: { label: "Close", status: "closed" },
  closed: { label: "Reopen", status: "published" },
};

export function JobAdminList() {
  const [jobs, setJobs] = useState<JobRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<JobRow | null>(null);
  const [deleteConfirmText, setDeleteConfirmText] = useState("");
  const [deleting, setDeleting] = useState(false);

  const [searchParams, setSearchParams] = useSearchParams();

  const fetchJobs = useCallback(() => {
    api
      .get<{ results: JobRow[] }>("/job-admin")
      .then((d) => setJobs(d.results))
      .catch((err) => setError(err instanceof Error ? err.message : "Failed to load jobs"))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    fetchJobs();
  }, [fetchJobs]);

  useEffect(() => {
    const msg = searchParams.get("success");
    if (msg) {
      setToast(msg);
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 4000);
    return () => clearTimeout(t);
  }, [toast]);

  const showToast = (msg: string) => setToast(msg);

  const copyJobUrl = (slug: string) => {
    const baseUrl = window.location.hostname.includes("internal")
      ? "https://talent.mntl.it"
      : window.location.origin;
    const url = `${baseUrl}/jobs/${slug}`;
    navigator.clipboard.writeText(url).then(
      () => showToast("Job URL copied to clipboard"),
      () => showToast(url),
    );
  };

  const changeStatus = async (slug: string, status: string) => {
    try {
      await api.patch(`/job-admin/${slug}/status`, { status });
      fetchJobs();
      showToast(`Job ${status === "published" ? "published" : status}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to update status");
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const force = deleteTarget.candidateCount > 0 ? "?force=true" : "";
      await api.delete(`/job-admin/${deleteTarget.slug}${force}`);
      setDeleteTarget(null);
      setDeleteConfirmText("");
      fetchJobs();
      showToast(`"${deleteTarget.title}" deleted`);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to delete job");
    } finally {
      setDeleting(false);
    }
  };

  const needsTypedConfirm = deleteTarget && deleteTarget.candidateCount > 0;
  const deleteReady = needsTypedConfirm
    ? deleteConfirmText === deleteTarget.title
    : deleteTarget !== null;

  return (
    <div className="space-y-6">
      {/* Toast */}
      {toast && (
        <div className="rounded-md bg-green-50 border border-green-200 px-4 py-3 text-sm text-green-800">
          {toast}
        </div>
      )}

      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Jobs</h1>
          <p className="text-sm text-gray-500 mt-1">Manage job postings</p>
        </div>
        <Link
          to="/jobs/new"
          className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-[var(--action-primary-hover)] transition-colors"
        >
          New Job
        </Link>
      </div>

      {error && (
        <div className="rounded-md bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
          <button onClick={() => setError(null)} className="ml-2 underline">
            Dismiss
          </button>
        </div>
      )}

      {loading ? (
        <div className="text-gray-400 animate-pulse">Loading...</div>
      ) : jobs.length === 0 ? (
        <div className="rounded-lg border border-gray-200 bg-white p-8 text-center text-gray-500">
          No jobs yet.{" "}
          <Link to="/jobs/new" className="text-primary-700 hover:text-primary-800">
            Create your first job posting
          </Link>
          .
        </div>
      ) : (
        <div className="rounded-lg border border-gray-200 bg-white overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                <th className="px-4 py-3">Title</th>
                <th className="px-4 py-3">Team</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Hiring Manager</th>
                <th className="px-4 py-3">Assignment</th>
                <th className="px-4 py-3 text-right">Candidates</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {jobs.map((job) => {
                const next = NEXT_STATUS[job.status];
                return (
                  <tr key={job.slug} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-3">
                      <Link
                        to={`/jobs/${job.slug}/edit`}
                        className="font-medium text-primary-700 hover:text-primary-900"
                      >
                        {job.title}
                      </Link>
                      <div className="text-xs text-gray-400">{job.location}</div>
                    </td>
                    <td className="px-4 py-3 text-gray-600">{job.team}</td>
                    <td className="px-4 py-3">
                      <span
                        className={`inline-block rounded-full px-2.5 py-0.5 text-xs font-medium ${STATUS_COLORS[job.status] ?? "bg-gray-100 text-gray-600"}`}
                      >
                        {job.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-gray-600 text-xs" title={job.hiringManager}>
                      {job.hiringManagerName}
                    </td>
                    <td className="px-4 py-3 text-xs">
                      {job.assignmentId && job.assignmentPdfPath ? (
                        <a
                          href={`/uploads/${job.assignmentPdfPath}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-primary-700 hover:text-primary-800"
                        >
                          {job.assignmentTitle}
                        </a>
                      ) : (
                        <span className="text-gray-400">&mdash;</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right text-gray-600 tabular-nums">
                      {job.candidateCount}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-2">
                        {job.status === "published" && (
                          <button
                            onClick={() => copyJobUrl(job.slug)}
                            className="text-xs font-medium text-gray-400 hover:text-gray-600"
                            title="Copy applicant URL"
                          >
                            Link
                          </button>
                        )}
                        <Link
                          to={`/jobs/${job.slug}/edit`}
                          className="text-xs font-medium text-primary-700 hover:text-primary-800"
                        >
                          Edit
                        </Link>
                        {next && (
                          <button
                            onClick={() => changeStatus(job.slug, next.status)}
                            className="text-xs font-medium text-gray-500 hover:text-gray-800"
                          >
                            {next.label}
                          </button>
                        )}
                        <button
                          onClick={() => {
                            setDeleteTarget(job);
                            setDeleteConfirmText("");
                          }}
                          className="text-xs font-medium text-red-500 hover:text-red-700"
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Delete confirmation dialog */}
      {deleteTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl space-y-4">
            <h2 className="text-lg font-semibold text-gray-900">Delete job</h2>
            <p className="text-sm text-gray-600">
              Are you sure you want to delete <strong>{deleteTarget.title}</strong>?
            </p>

            {deleteTarget.candidateCount > 0 && (
              <div className="rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-800">
                This job has <strong>{deleteTarget.candidateCount}</strong> candidate(s). Deleting
                this job will <strong>permanently remove all associated candidates</strong>, their
                applications, notes, and stage history. This cannot be undone. Type the job title to
                confirm:
                <input
                  type="text"
                  value={deleteConfirmText}
                  onChange={(e) => setDeleteConfirmText(e.target.value)}
                  placeholder={deleteTarget.title}
                  className="mt-2 block w-full rounded-md border border-red-300 bg-white px-3 py-1.5 text-sm focus:border-red-500 focus:ring-1 focus:ring-red-500 focus:outline-none"
                />
              </div>
            )}

            <div className="flex items-center justify-end gap-3">
              <button
                onClick={() => {
                  setDeleteTarget(null);
                  setDeleteConfirmText("");
                }}
                className="text-sm text-gray-500 hover:text-gray-700"
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                disabled={!deleteReady || deleting}
                className="rounded-md bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50 transition-colors"
              >
                {deleting ? "Deleting..." : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
